import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Server, Database, Activity, AlertTriangle, CheckCircle2, Clock, Globe, ListChecks, Zap, Gauge } from "lucide-react";
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

function fmtUptime(sec) {
  sec = Math.max(0, Math.floor(sec || 0));
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d >= 30) { const mo = Math.floor(d / 30), rd = d % 30; return `${mo}ay${rd ? ` ${rd}g` : ""}`; }
  if (d >= 1) return `${d}g${h ? ` ${h}sa` : ""}`;
  if (h >= 1) return `${h}sa${m ? ` ${m}dk` : ""}`;
  return `${m}dk`;
}

function Stat({ icon: Icon, label, value, sub, tone = "default", testid }) {
  const toneCls = {
    default: "text-foreground",
    ok: "text-emerald-400",
    warn: "text-amber-400",
    err: "text-red-400",
    info: "text-blue-400",
  }[tone];
  return (
    <div className="kpi-card" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{label}</div>
        <Icon size={14} className="text-muted-foreground" />
      </div>
      <div className={`mt-2 text-3xl font-semibold tracking-tight ${toneCls}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [throughput, setThroughput] = useState([]);
  const [queryStats, setQueryStats] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [resolverNodes, setResolverNodes] = useState([]);

  const load = async () => {
    try {
      const [s, t, q, e, r] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/dashboard/throughput"),
        api.get("/dashboard/query-stats"),
        api.get("/events", { params: { limit: 10 } }),
        api.get("/dashboard/resolver-stats").catch(() => ({ data: { nodes: [] } })),
      ]);
      setStats(s.data && typeof s.data === "object" ? s.data : null);
      setThroughput(Array.isArray(t.data) ? t.data : []);
      setQueryStats(Array.isArray(q.data) ? q.data : []);
      setRecentEvents(Array.isArray(e.data) ? e.data : []);
      setResolverNodes(Array.isArray(r.data?.nodes) ? r.data.nodes : []);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, []);

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Genel Bakış</h1>
          <p className="text-sm text-muted-foreground mt-1">DNS orkestrasyon sistemi durumu · canlı</p>
        </div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
          15sn'de bir yenilenir
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat
          icon={Server}
          label="WHM Sunucuları"
          value={stats ? `${stats.whm_online}/${stats.whm_count}` : "—"}
          sub="çevrimiçi / toplam"
          tone={stats && stats.whm_count > 0 && stats.whm_online === stats.whm_count ? "ok" : stats && stats.whm_count === 0 ? "default" : "warn"}
          testid="stat-whm"
        />
        <Stat
          icon={Database}
          label="PowerDNS"
          value={stats ? `${stats.pdns_healthy}/${stats.pdns_count}` : "—"}
          sub="sağlıklı / toplam"
          tone={stats && stats.pdns_count > 0 && stats.pdns_healthy === stats.pdns_count ? "ok" : stats && stats.pdns_count === 0 ? "default" : "err"}
          testid="stat-pdns"
        />
        <Stat
          icon={Activity}
          label="24s Olay"
          value={stats?.events_24h ?? "—"}
          sub="toplam event"
          tone="info"
          testid="stat-events"
        />
        <Stat
          icon={Globe}
          label="Zone Sayısı"
          value={stats?.zones_total ?? "—"}
          sub="izlenen domain"
          testid="stat-zones"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Stat
          icon={Clock}
          label="Bekleyen"
          value={stats?.queue_pending ?? "—"}
          sub="kuyruk + retry + running"
          tone="warn"
          testid="stat-pending"
        />
        <Stat
          icon={AlertTriangle}
          label="Başarısız"
          value={stats?.queue_failed ?? "—"}
          sub="max retry'e ulaşan"
          tone={stats?.queue_failed > 0 ? "err" : "default"}
          testid="stat-failed"
        />
        <Stat
          icon={CheckCircle2}
          label="24s Tamamlandı"
          value={stats?.queue_done_24h ?? "—"}
          sub="başarılı senkron"
          tone="ok"
          testid="stat-done"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Stat
          icon={Activity}
          label="Toplam Sorgu"
          value={stats ? (stats.total_queries ?? 0).toLocaleString("tr-TR") : "—"}
          sub="udp + tcp · başlangıçtan beri"
          tone="info"
          testid="stat-total-queries"
        />
        <Stat
          icon={Zap}
          label="Cache'den Sorgu"
          value={stats ? (stats.cache_hits ?? 0).toLocaleString("tr-TR") : "—"}
          sub="packet cache hit"
          tone="ok"
          testid="stat-cache-hits"
        />
        <Stat
          icon={Gauge}
          label="Cache Oranı"
          value={stats ? `%${(stats.cache_ratio ?? 0).toLocaleString("tr-TR")}` : "—"}
          sub="cache'den karşılanan sorgu"
          tone={stats && stats.cache_ratio >= 50 ? "ok" : stats && stats.cache_ratio > 0 ? "warn" : "default"}
          testid="stat-cache-ratio"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="border border-border bg-card rounded-md p-4" data-testid="throughput-panel">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-semibold">Saatlik Senkron Hacmi</h2>
            <div className="text-xs text-muted-foreground">son 24 saat</div>
          </div>
          <ListChecks size={14} className="text-muted-foreground" />
        </div>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={throughput}>
              <CartesianGrid stroke="rgba(255,255,255,0.04)" strokeDasharray="3 3" />
              <XAxis dataKey="hour" stroke="#6b7280" tick={{ fontFamily: "JetBrains Mono", fontSize: 10 }} />
              <YAxis stroke="#6b7280" tick={{ fontFamily: "JetBrains Mono", fontSize: 10 }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: "#0D1222", border: "1px solid #1E2A4A", fontSize: 12 }}
                labelStyle={{ color: "#9CA3AF", fontFamily: "JetBrains Mono" }}
              />
              <Legend wrapperStyle={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
              <Line type="monotone" dataKey="done" stroke="#10B981" strokeWidth={2} dot={false} name="tamamlandı" />
              <Line type="monotone" dataKey="failed" stroke="#EF4444" strokeWidth={2} dot={false} name="başarısız" />
              <Line type="monotone" dataKey="other" stroke="#3B82F6" strokeWidth={2} dot={false} name="bekleyen" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="border border-border bg-card rounded-md p-4" data-testid="query-stats-panel">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-semibold">Sorgu Hacmi (Cache / Backend)</h2>
            <div className="text-xs text-muted-foreground">son 24 saat · saatlik</div>
          </div>
          <Zap size={14} className="text-muted-foreground" />
        </div>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={queryStats}>
              <defs>
                <linearGradient id="gCached" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.5} />
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gUncached" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.5} />
                  <stop offset="95%" stopColor="#F59E0B" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.04)" strokeDasharray="3 3" />
              <XAxis dataKey="hour" stroke="#6b7280" tick={{ fontFamily: "JetBrains Mono", fontSize: 10 }} />
              <YAxis stroke="#6b7280" tick={{ fontFamily: "JetBrains Mono", fontSize: 10 }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: "#0D1222", border: "1px solid #1E2A4A", fontSize: 12 }}
                labelStyle={{ color: "#9CA3AF", fontFamily: "JetBrains Mono" }}
              />
              <Legend wrapperStyle={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
              <Area type="monotone" dataKey="cached" stackId="1" stroke="#10B981" strokeWidth={2} fill="url(#gCached)" name="cache'den" />
              <Area type="monotone" dataKey="uncached" stackId="1" stroke="#F59E0B" strokeWidth={2} fill="url(#gUncached)" name="backend'den" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
      </div>

      <div className="border border-border bg-card rounded-md p-4" data-testid="resolver-panel">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-semibold">Public DNS (Resolver)</h2>
            <div className="text-xs text-muted-foreground">dns.netlen.com.tr · node kullanımı</div>
          </div>
          <Server size={14} className="text-muted-foreground" />
        </div>
        {resolverNodes.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground font-mono">henüz veri yok — resolver agent'ları gönderim yapmıyor</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {resolverNodes.map((n) => (
              <div key={n.node} className="border border-border rounded-md p-3" data-testid={`resolver-${n.node}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-sm font-semibold">{n.node}</span>
                  <span className={`status-badge ${n.online ? "ok" : "err"}`}>{n.online ? "çevrimiçi" : "çevrimdışı"}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <div className="text-lg font-semibold text-blue-400">{Math.round(n.qps)}</div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">QPS</div>
                  </div>
                  <div title={`Son 24 saatte işlenen sorgu${n.queries_24h_partial ? " (henüz 24s veri dolmadı — kısmi)" : ""} · başlangıçtan beri toplam: ${(n.questions_total ?? 0).toLocaleString("tr-TR")}`}>
                    <div className="text-lg font-semibold">{n.queries_24h_partial ? "~" : ""}{(n.queries_24h ?? 0).toLocaleString("tr-TR")}</div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">24s Sorgu</div>
                  </div>
                  <div>
                    <div className={`text-lg font-semibold ${n.cache_ratio >= 40 ? "text-emerald-400" : "text-amber-400"}`}>%{n.cache_ratio}</div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Cache</div>
                  </div>
                </div>
                <div className="mt-2 text-[10px] font-mono text-muted-foreground text-right">
                  uptime {fmtUptime(n.uptime)} · son görülme {n.last_seen}sn önce
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border border-border bg-card rounded-md p-4" data-testid="recent-events">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold">Son Olaylar</h2>
          <a href="/events" className="text-xs text-primary hover:underline" data-testid="link-all-events">tümü →</a>
        </div>
        {recentEvents.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground font-mono">kayıt yok</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Zaman</th>
                <th>İşlem</th>
                <th>Domain</th>
                <th>Durum</th>
                <th>Mesaj</th>
              </tr>
            </thead>
            <tbody>
              {recentEvents.map((ev) => (
                <tr key={ev.id}>
                  <td className="font-mono text-xs text-muted-foreground">{new Date(ev.created_at).toLocaleString("tr-TR")}</td>
                  <td className="font-mono text-xs">{ev.action}</td>
                  <td className="font-mono text-xs">{ev.domain || "—"}</td>
                  <td>
                    <span className={`status-badge ${ev.status === "success" ? "ok" : ev.status === "error" ? "err" : "info"}`}>
                      {ev.status}
                    </span>
                  </td>
                  <td className="text-xs text-muted-foreground truncate max-w-md">{ev.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
